import type { PropertySelectorOption } from '~/components/filters/advanced/types'
import {
  BooleanOperators,
  CustomFieldStringOperators,
  DomainOperators,
  EmptyCheckOperators,
  type FilterGroup,
  MultiSelectDomainOperators,
  NumericOperators,
  type Operator,
  operatorRequiresValue,
  StringOperators
} from '~/types/filters'
import type { AssetsSTC, State, Telemetry } from '~/types/capabilities'
import { type ColorConfig, type CustomField, UNKNOWN_COLOR_CONFIG } from '~/types/asset'
import { isHexColor } from '~/helpers/colors'

export const getSelectedProperty = (
  options: PropertySelectorOption[],
  propertyId: string
): PropertySelectorOption | undefined => {
  for (const option of options) {
    if ('children' in option && option.children) {
      const result = getSelectedProperty(option.children, propertyId)
      if (result) {
        return result
      }
    } else if (option.id === propertyId) {
      return option
    }
  }

  return undefined
}

export const getOperatorsForType = (type: string): Operator[] => {
  if (type === 'number' || type === 'date' || type === 'float') {
    return Object.values(NumericOperators)
  }

  if (type === 'select') {
    return Object.values(DomainOperators)
  }

  if (type === 'multi-select') {
    return Object.values(MultiSelectDomainOperators)
  }

  if (type === 'boolean') {
    return Object.values(BooleanOperators)
  }

  if (type === 'custom-field-text') {
    return Object.values(CustomFieldStringOperators)
  }

  if (type === 'custom-field-number') {
    return [...Object.values(NumericOperators), ...Object.values(EmptyCheckOperators)]
  }

  return Object.values(StringOperators)
}

export const getTelemetriesProperties = (dict?: AssetsSTC): PropertySelectorOption[] => {
  if (!dict || !dict.data) {
    return []
  }
  const properties: PropertySelectorOption[] = []

  for (const assetSTC of dict.data) {
    for (const telemetry of assetSTC.telemetry || []) {
      const existingItem = properties.find((item) => item.id === telemetry.id)

      if (!existingItem) {
        properties.push({
          id: telemetry.id,
          label: `telemetries.${telemetry.id}`,
          type: getPropertyType(telemetry),
          group: 'telemetries',
          operators: getOperatorsForType(telemetry.type),
          operandType: 'CAPABILITIES',
          unit: telemetry.unit
        })
      }
    }
  }

  return filterSTCForContext(properties, 'default')
}

export const getStatesProperties = (dict?: AssetsSTC): PropertySelectorOption[] => {
  if (!dict || !dict.data) {
    return []
  }
  const properties: PropertySelectorOption[] = []

  for (const assetSTC of dict.data) {
    for (const state of assetSTC.states || []) {
      const existingItem = properties.find((item) => item.id === state.id)

      if (!existingItem) {
        properties.push({
          id: state.id,
          label: `telemetries.${state.id}`,
          type: getPropertyType(state),
          group: 'states',
          operandType: 'CAPABILITIES',
          operators: getOperatorsForType(Array.isArray(state.values) ? 'select' : state.type),
          options: Array.isArray(state.values)
            ? state.values.map((value) => ({
                value,
                label: value
              }))
            : undefined,
          unit: state.unit
        })
      }
    }
  }

  return filterSTCForContext(properties, 'default')
}

export const getStatesAndTelemetriesProperties = (dict?: AssetsSTC): PropertySelectorOption[] => {
  if (!dict || !dict.data) {
    return []
  }
  const states = getStatesProperties(dict)
  const telemetries = getTelemetriesProperties(dict)
  return [...states, ...telemetries].sort((a, b) => a.label.localeCompare(b.label))
}

export const getCustomFieldsProperties = (dict?: AssetsSTC): PropertySelectorOption[] => {
  if (!dict || !dict.data) {
    return []
  }
  const properties: PropertySelectorOption[] = []
  const seenIds = new Set<string>()

  for (const assetSTC of dict.data) {
    for (const customField of assetSTC.customFields || []) {
      const idStr = String(customField.name)
      if (!seenIds.has(idStr)) {
        seenIds.add(idStr)
        properties.push({
          id: customField.name,
          label: `customFields.${customField.nameLocalizedKey}`,
          type: getCustomFieldPropertyType(customField),
          operandType: 'CUSTOM_FIELDS',
          operators: getOperatorsForType(getCustomFieldPropertyType(customField))
        })
      }
    }
  }

  return filterSTCForContext(properties, 'default')
}

export const getCustomFieldPropertyType = (customField: CustomField): 'custom-field-number' | 'custom-field-text' => {
  // Todo: Add support for boolean and dates custom fields
  if (customField.type === 'number') {
    return 'custom-field-number'
  }

  return 'custom-field-text'
}

export const getPropertyType = (item: Telemetry | State): PropertySelectorOption['type'] => {
  if (item.type === 'float') {
    return 'float'
  }

  if (item.type === 'number' || item.type === 'signedNumber') {
    return 'number'
  }

  if (Array.isArray((item as State).values)) {
    return 'select'
  }

  return 'text'
}

export const cleanEmptyClauses = (group: FilterGroup): FilterGroup => {
  return {
    ...group,
    children: group.children
      .map((child) => {
        if (child.type === 'group') {
          return cleanEmptyClauses(child)
        }
        return child
      })
      .filter((child) => {
        if (child.type === 'clause') {
          // Operators like IS_EMPTY/IS_NOT_EMPTY don't require values
          if (!operatorRequiresValue(child.operator)) {
            return true
          }

          return child.value !== undefined && child.value !== null && child.value !== ''
        } else if (child.type === 'group') {
          return child.children.length > 0
        }
        return false
      })
  }
}

export const getMapColorValueById = (id: number, mapIconColors?: ColorConfig[]): string => {
  if (!mapIconColors) {
    return UNKNOWN_COLOR_CONFIG
  }
  const colorConfig = mapIconColors.find((color) => color.id === id)
  return colorConfig && isHexColor(colorConfig.colorName) ? colorConfig.colorName : UNKNOWN_COLOR_CONFIG
}

export const filterSTCForContext = (options: PropertySelectorOption[], _context: string): PropertySelectorOption[] => {
  // TODO: implement context filtering. We currently filter some data for the MEP
  const MEP_FILTER = [
    'LNLV_MODULE_LOCK_STATE',
    'ALARM_RESTORE_POWER_ON',
    'ALARM_TILT_ANGLE',
    'ALARM_HW_LAST_GASP',
    'ALARM_LOST_MODULE',
    'ALARM_VOLTAGE',
    'ALARM_CONTEXTUAL_VALUE',
    'ALARM_TRIGGER_VALUE',
    'ALARM_CONDITION',
    'ALARM_OFF_NOTIFICATION_TYPE',
    'ALARM_ON_NOTIFICATION_TYPE',
    'ALARM_SOURCE_TYPE',
    'SEQUENCE_NUMBER',
    'LNLV_TIMESTAMP',
    'FONT',
    'ALIGNMENT',
    'ISO_TS',
    'COMMAND_TIMESTAMP',
    'TIME_STAMP',
    'GATEWAY_RESPONSE',
    'ERROR_ACK',
    'ERROR_FLAG',
    'PARAMETER_CATEGORY',
    'SDK_BUILD',
    'SDK_MINOR',
    'SDK_MAJOR',
    'VERSION_APP_PATCH',
    'VERSION_APP_MINOR',
    'VERSION_APP_MAJOR',
    'DATA',
    'PARAMETER_ID',
    'FILE_CRC',
    'SLOT_TWO_CRC',
    'SLOT_ONE_CRC',
    'SLOT_ZERO_CRC',
    'SLOT_TWO_VERSION',
    'SLOT_ONE_VERSION',
    'SLOT_ZERO_VERSION',
    'SLOT_TWO_VALID',
    'SLOT_ONE_VALID',
    'SLOT_ZERO_VALID',
    'ACTIVE_SLOT',
    'ALARM_LAST_GASP',
    'ACCESS_CODE',
    'NONCE',
    'SYSTEM_EVENT_ID',
    'RESET_REQUEST_SOURCE',
    'BLE_STATE_VALUE',
    'BIN_TLV_VERSION',
    'RETURN_CODE',
    'WHITEBOARD_SIZE',
    'CONNECTIVITY_TYPE',
    'RSSI',
    'UPTIME',
    'CUMULATIVE_UPTIME',
    'GPS_LOCATION',
    'GATEWAY_TIME',
    'MODEM_FIRMWARE_VERSION',
    'RADIO_FIRMWARE_VERSION',
    'FILE_SYSTEM_VERSION',
    'MESH_NETWORK_ID',
    'BASE_TIME',
    'TIME_DELTA_BETWEEN_RECORDS',
    'NUM_RECORDS',
    'CONSUMPTION_ENERGY',
    'ACTION_ID',
    'MAC_ADDRESS',
    'NODE_IP_ADDRESS',
    'PRIMARY_PARENT_ADDRESS',
    'SECONDARY_PARENT_ADDRESS',
    'EDGE_ROUTER_ADDRESS',
    'LAMP_ON_OFF_STATUS',
    'LATEST_PARENT_RSSI',
    'LATEST_NETWORK_UTILIZATION',
    'NETWORK_STATS',
    'ALERT_REASON',
    'ALERT_FLAG',
    'ALERT_VALUE',
    'NIC_INFO_CURRENT_VERSION',
    'NIC_INFO_NEW_VERSION',
    'NIC_BOOTSTRAP_INFO_CURRENT_VERSION',
    'NIC_BOOTSTRAP_INFO_NEW_VERSION',
    'NIC_BOOTLOADER_INFO_CURRENT_VERSION',
    'NIC_BOOTLOADER_INFO_NEW_VERSION',
    'MAIN_HW_REVISION',
    'NIC_HW_REVISION',
    'AFE_HW_REVISION',
    'MAIN_MCU_INFO_CURRENT_VERSION',
    'MAIN_MCU_INFO_NEW_VERSION',
    'MAIN_MCU_BOOTLOADER_INFO_CURRENT_VERSION',
    'MAIN_MCU_BOOTLOADER_INFO_NEW_VERSION',
    'AFE_INFO_CURRENT_VERSION',
    'AFE_INFO_NEW_VERSION',
    'GPS_INFO_CURRENT_VERSION',
    'GPS_INFO_NEW_VERSION',
    'LAMP_DIM_LEVEL',
    'LAMP_DIM_LEVEL_CHAR',
    'LAMP_ON_TIME',
    'MAC_PERIOD',
    'RPM_PERIOD',
    'MCU_STATS_PERIOD',
    'GPS_PERIOD',
    'ENERGY_SAMPLING_PERIOD',
    'ENERGY_REPORTING_PERIOD',
    'METROLOGY_REPORTING_PERIOD',
    'LUX_LEVEL_REPORTING_PERIOD',
    'RELAY_ALERT_SETTING_STRUCT_CHECK_PERIOD_IN_MIN',
    'RELAY_ALERT_SETTING_STRUCT_MAX_OFF_POWER',
    'RELAY_ALERT_SETTING_STRUCT_MIN_ON_POWER',
    'ALS_LIGHT_ALERT_SETTING_STRUCT_CHECK_PERIOD_IN_MIN',
    'ALS_LIGHT_ALERT_SETTING_STRUCT_MAX_MISMATCH_TIME_IN_MIN',
    'LIGHT_FLICKERING_ALERT_SETTING_STRUCT_CHECK_PERIOD_IN_MIN',
    'LIGHT_FLICKERING_ALERT_SETTING_STRUCT_MAX_LIGHT_TRANSITIONS',
    'VOLTAGE_ERROR_ALERT_SETTING_STRUCT_CHECK_PERIOD_IN_MIN',
    'VOLTAGE_ERROR_ALERT_SETTING_STRUCT_MIN_VOLTAGE',
    'VOLTAGE_ERROR_ALERT_SETTING_STRUCT_MAX_VOLTAGE',
    'POWER_FACTOR_ALERT_SETTING_STRUCT_CHECK_PERIOD_IN_MIN',
    'POWER_FACTOR_ALERT_SETTING_STRUCT_MIN_PF',
    'OVER_POWER_ALERT_SETTING_STRUCT_CHECK_PERIOD_IN_MIN',
    'OVER_POWER_ALERT_SETTING_STRUCT_MAX_POWER',
    'TEMP_ERR_ALERT_SETTING_STRUCT_CHECK_PERIOD_IN_MIN',
    'TEMP_ERR_ALERT_SETTING_STRUCT_MAX_TEMP',
    'TEMP_ERR_ALERT_SETTING_STRUCT_MIN_TEMP',
    'ALS_MALFUNCTION_ALERT_SETTING_STRUCT_CHECK_PERIOD_IN_MIN',
    'ALS_MALFUNCTION_ALERT_SETTING_STRUCT_ALERT_THRESHOLD_MINUTES',
    'GPS_MALFUNCTION_ALERT_SETTING_STRUCT_CHECK_PERIOD_IN_MIN',
    'GPS_MALFUNCTION_ALERT_SETTING_STRUCT_ALERT_THRESHOLD_MINUTES',
    'CLO_GAIN_VALUE',
    'ALS_ON_ADC_LEVEL',
    'ALS_OFF_ADC_LEVEL',
    'DST_MODE',
    'DST_MODE_ENUM',
    'DST_ENABLED',
    'DST_ENABLED_STATE',
    'DST_START_TIME_DAYS_OFFSET',
    'DST_START_TIME_OCCURRENCE_NUMBER',
    'DST_START_TIME_DAY_OF_WEEK',
    'DST_START_TIME_DAY_OF_WEEK_ENUM',
    'DST_START_TIME_MONTH_OF_YEAR',
    'DST_START_TIME_MONTH_OF_YEAR_ENUM',
    'DST_START_TIME_HOUR_OF_DAY',
    'DST_START_TIME_MINUTE_OF_HOUR',
    'DST_END_TIME_DAYS_OFFSET',
    'DST_END_TIME_OCCURRENCE_NUMBER',
    'DST_END_TIME_DAY_OF_WEEK',
    'DST_END_TIME_DAY_OF_WEEK_ENUM',
    'DST_END_TIME_MONTH_OF_YEAR',
    'DST_END_TIME_MONTH_OF_YEAR_ENUM',
    'DST_END_TIME_HOUR_OF_DAY',
    'DST_END_TIME_MINUTE_OF_HOUR'
  ]
  return options.filter((item: PropertySelectorOption) => {
    return !MEP_FILTER.includes(item.id)
  })
}
